import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray } from '../transformer';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';

type BannerHeaderSliceState = {
  loading: boolean;
  listBannerHeader: BannerHeader[];
  screenFunctions: ScreenFunction[];
  displayTypes: DisplayType[];
  message?: MessageType;
  pagination: Pagination;
  bannerHeaderDetail?: BannerHeaderDetail;
};

const initialState: BannerHeaderSliceState = {
  loading: false,
  listBannerHeader: [],
  screenFunctions: [],
  displayTypes: [],
  pagination: PAGINATION
};

export const bannerHeaderSlice = createAppSlice({
  name: 'bannerHeader',
  initialState,
  reducers: (create) => ({
    getListBannerHeader: create.asyncThunk(
      async ({
        query,
        offset,
        limit
      }: {
        query?: BannerHeaderListFilter;
        offset?: number;
        limit?: number;
      }) => {
        const body = {
          ...(query || {}),
          PAGE: offset ?? 1,
          LIMIT: limit ?? 10
        };

        const response = await apiFetcher.post('header-banners/list', body);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBannerHeader = transformerArray(action.payload.listData);
          state.pagination = action.payload.pagination;
        },
        rejected: (state) => {
          state.loading = false;
          state.listBannerHeader = [];
          state.pagination = PAGINATION;
        }
      }
    ),

    createBannerHeader: create.asyncThunk(
      async (body: CreateBannerHeaderPayload) => {
        await apiFetcher.post('header-banners', body);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_BANNER_HEADER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_BANNER_HEADER_FAIL
          };
        }
      }
    ),

    updateBannerHeader: create.asyncThunk(
      async (body: UpdateBannerHeaderPayload) => {
        await apiFetcher.put('header-banners', body);
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_BANNER_HEADER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.UPDATE_BANNER_HEADER_FAIL
          };
        }
      }
    ),

    deleteBannerHeader: create.asyncThunk(
      async (id: number) => {
        await apiFetcher.delete(`header-banners/${id}`);
        return id;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listBannerHeader = state.listBannerHeader.filter(
            (bannerHeader) => bannerHeader.id !== action.payload
          );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_BANNER_HEADER_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_BANNER_HEADER_FAIL
          };
        }
      }
    ),

    getBannerHeaderById: create.asyncThunk(
      async (id: string) => {
        const response = await apiFetcher.get(`header-banners/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          const payload = action.payload;
          const transformedDetails = transformerArray(
            payload.DETAILS || []
          ).map((detail: BannerHeaderDetailItem) => ({
            ...detail,
            enabled: Boolean(detail.enabled)
          }));
          const transformedDetail: BannerHeaderDetail = {
            id: payload.ID,
            title: payload.TITLE,
            details: transformedDetails
          };
          state.bannerHeaderDetail = transformedDetail;
        },
        rejected: (state) => {
          state.loading = false;
          state.bannerHeaderDetail = undefined;
        }
      }
    ),

    resetMessages: create.reducer((state) => {
      state.message = undefined;
    }),

    getScreenFunctions: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('screen-functions');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.screenFunctions = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
        }
      }
    ),

    toggleBannerHeaderPlatform: create.asyncThunk(
      async ({
        id,
        type,
        status
      }: {
        id: number;
        type: 'PC' | 'MOBILE' | 'APP';
        status: boolean;
      }) => {
        const body = {
          ID: id,
          TYPE: type,
          STATUS: status
        };

        const response = await apiFetcher.post('header-banners/disable', body);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
        },
        rejected: (state) => {
          state.loading = false;
        }
      }
    ),

    getDisplayTypes: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('header-banners/display-types');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.displayTypes = transformerArray(action.payload);
        },
        rejected: (state) => {
          state.loading = false;
          state.displayTypes = [];
        }
      }
    )
  }),

  selectors: {
    watchListBannerHeader: (bannerHeader) => bannerHeader.listBannerHeader,
    watchBannerHeaderDetail: (bannerHeader) => bannerHeader.bannerHeaderDetail,
    watchScreenFunctions: (bannerHeader) => bannerHeader.screenFunctions,
    watchDisplayTypes: (bannerHeader) => bannerHeader.displayTypes,
    watchBannerHeaderMessage: (bannerHeader) => bannerHeader.message,
    watchBannerHeaderLoading: (bannerHeader) => bannerHeader.loading,
    watchBannerHeaderPagination: (bannerHeader) => bannerHeader.pagination
  }
});

export const {
  watchListBannerHeader,
  watchBannerHeaderMessage,
  watchBannerHeaderDetail,
  watchScreenFunctions,
  watchDisplayTypes,
  watchBannerHeaderLoading,
  watchBannerHeaderPagination
} = bannerHeaderSlice.selectors;

export const {
  getListBannerHeader,
  createBannerHeader,
  updateBannerHeader,
  deleteBannerHeader,
  getBannerHeaderById,
  getScreenFunctions,
  getDisplayTypes,
  resetMessages,
  toggleBannerHeaderPlatform
} = bannerHeaderSlice.actions;
